import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { useUploadDocument } from "../../api/RegistrationApi";
import { FileUploadDisplay } from "./FileUploadDisplay";
import RadioGroup from "../common/RadioGroup";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { useNotification } from "../../common/context/NotificationProvider";
import { DocumentType } from "../../model/UploadType";
import { UPLOAD_STATUS } from "../../common/constants/navigationConstants";
import { DOCUMENT_TYPE_RADIO_GROUP_OPTIONS } from "../../common/data/sifarnik";
import { useUploadImages } from "../../api/UploadApi";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { Dialog, DialogContent } from "@mui/material";
import { useMediaQuery} from "react-responsive";
import hourglassImg from '../../images/OTP-icon-96x96-hourglass.svg';
import { Trans, useTranslation } from "react-i18next";

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg'];
const ALLOWED_FILE_TYPES_STRING = 'JPEG';

type UploadError = "type" | "size" | "missing-files";

const UploadPage = () => {
    const { t } = useTranslation();
    const [documentType, setDocumentType] = useState<DocumentType>("osobna");
    const [frontImage, setFrontImage] = useState<File | null>(null);
    const [backImage, setBackImage] = useState<File | null>(null);
    const [error, setError] = useState<UploadError | null>(null);
    const { uploadInformationCallback, getZahtjevId, restoreContext, getStatus } = useContext(ZahtjevContext);
    const currentStatus = getStatus();
    const pageStatus = UPLOAD_STATUS;
    const { isPending: uploadImagesIsLoading, mutate: uploadImagesMutation } = useUploadImages();
    const { isPending: uploadDocumentIsLoading, mutate: uploadDocumentMutation } = useUploadDocument(getZahtjevId());
    const isLoading = uploadImagesIsLoading || uploadDocumentIsLoading;

    const frontInputRef = useRef<HTMLInputElement>(null);
    const backInputRef = useRef<HTMLInputElement>(null);
    const { notify } = useNotification();
    const navigate = useNavigate();
    const [open, setOpen] = useState<boolean>(false);
    const isMobile = useMediaQuery({ maxWidth: 767 });

    const handleDocumentTypeChange = (value: string) => {
        setDocumentType(value as DocumentType);
        setFrontImage(null);
        setBackImage(null);
        setError(null);
        if (frontInputRef.current) frontInputRef.current.value = '';
        if (backInputRef.current) backInputRef.current.value = '';
    };

    const getErrorMessage = (): string | null => {
        switch (error) {
            case "type":
                return t("upload-page.identification-document.error.type", { type: ALLOWED_FILE_TYPES_STRING });
            case "size":
                return t("upload-page.identification-document.error.size", { max: MAX_FILE_SIZE_MB });
            case "missing-files":
                return t(`upload-page.identification-document.error.missing-files.${documentType === 'osobna' ? "ID-card" : "passport"}`);
            default:
                return null;
        }
    };

    const validateFile = (file: File | null): boolean => {
        if (!file) return false;
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            setError("type");
            return false;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setError("size");
            return false;
        }
        setError(null);
        return true;
    };

    const handleFileChange = (
      event: ChangeEvent<HTMLInputElement>,
      target: 'front' | 'back'
    ) => {
        const file = event.target.files?.[0] || null;
        if (validateFile(file)) {
            if (target === 'front') setFrontImage(file);
            else if (target === 'back') setBackImage(file);
        } else {
            if (target === 'front') {
                setFrontImage(null);
                if (frontInputRef.current) frontInputRef.current.value = '';
            } else if (target === 'back') {
                setBackImage(null);
                if (backInputRef.current) backInputRef.current.value = '';
            }
        }
    };

    const handleDelete = (target: 'front' | 'back') => {
        if (target === 'front') {
            setFrontImage(null);
            if (frontInputRef.current) frontInputRef.current.value = '';
        } else {
            setBackImage(null);
            if (backInputRef.current) backInputRef.current.value = '';
        }
        setError(null);
    };

    const triggerFileInput = (ref: React.RefObject<HTMLInputElement | null>) => {
        ref.current?.click();
    };

    const handleNextClick = async () => {
        if (error === "type" || error === "size") {
            return;
        }

        if (!frontImage || (documentType === 'osobna' && !backImage)) {
            setError("missing-files");
            return;
        }

        const filesToUpload: File[] = [];
        if (frontImage) filesToUpload.push(frontImage);
        if (documentType === 'osobna' && backImage) filesToUpload.push(backImage);

        setOpen(true);

        uploadImagesMutation({ files: filesToUpload }, {
            onSuccess: () => {
                uploadDocumentMutation(undefined, {
                    onSuccess: () => {
                        setOpen(false)
                        uploadInformationCallback({ documentType, frontImage, backImage })
                        navigate('/personal-information');
                    }, onError: () => {
                        setOpen(false)
                        notify(ERROR_MESSAGE_NOTIFICATION, "error");
                    },
                });
            },
            onError: () => {
                setOpen(false)
                notify(ERROR_MESSAGE_NOTIFICATION, "error");
            },
        })
    };

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
      <Card title={t("upload-page.title")} className="flex flex-col"
            isLoading={parseInt(currentStatus) < parseInt(pageStatus)}>
          <div className="flex-grow">
              <RadioGroup
                handleValueChange={handleDocumentTypeChange}
                className="mb-6"
                name="document-type-group"
                options={DOCUMENT_TYPE_RADIO_GROUP_OPTIONS.map(option => {
                  return {
                    ...option,
                    label: t(option.label)
                  }
                })}
                label={t("upload-page.identification-document.label")}
                defaultValue={documentType}
              />

              <input
                type="file"
                ref={frontInputRef}
                onChange={(e) => handleFileChange(e, 'front')}
                accept={ALLOWED_FILE_TYPES.join(',')} hidden
              />

              {documentType === 'osobna' && (
                <input
                  type="file"
                  ref={backInputRef}
                  onChange={(e) => handleFileChange(e, 'back')}
                  accept={ALLOWED_FILE_TYPES.join(',')} hidden
                />
              )}

              <div className="w-full max-w-md space-y-4 mb-6">
                  <FileUploadDisplay
                    onClick={() => triggerFileInput(frontInputRef)}
                    label={
                      <Trans
                        i18nKey={`upload-page.identification-document.${documentType === 'osobna' ? "ID-card.frontside-description" : "passport.description"}`}
                        components={{ "click-here": <span className="text-[#52AE30] font-medium" /> }}
                      />
                    }
                    uploadedFile={frontImage}
                    onDelete={() => handleDelete('front')}
                    inputId="front-file-input"
                  />

                  {documentType === 'osobna' && (
                    <FileUploadDisplay
                      onClick={() => triggerFileInput(backInputRef)}
                      label={
                        <Trans
                          i18nKey="upload-page.identification-document.ID-card.backside-description"
                          components={{ "click-here": <span className="text-[#52AE30] font-medium" /> }}
                        />
                      }
                      uploadedFile={backImage}
                      onDelete={() => handleDelete('back')}
                      inputId="back-file-input"
                    />
                  )}
              </div>

              {error ? (
                <p className="text-red-500 text-sm -mt-2 mb-4" role="alert" data-field-error="true">{getErrorMessage()}</p>
              ) : (
                <p className="text-gray-500 text-sm mt-2">{t("upload-page.identification-document.size-description", { max: MAX_FILE_SIZE_MB, type: ALLOWED_FILE_TYPES_STRING })}</p>
              )}


              <p className="text-gray-500 text-sm mt-2">
                  {t("upload-page.identification-document.info")}
              </p>
          </div>

          <div className="mt-8 text-center md:text-left">
              <Button text={t("common.button-next")} disabled={isLoading} onClick={handleNextClick} isLoading={isLoading} />
          </div>

          <Dialog open={open} PaperProps={{sx: {p: 3, borderRadius: 4, minWidth: isMobile ? 180 : 280}}}>
              <DialogContent sx={{ pb: 2 }}>
                  {t("upload-page.upload-in-process.info")}
                  <br/>
                  <br/>
                  {t("upload-page.upload-in-process.instruction")}
                  <div className="flex justify-center">
                  <img className="mt-6" src={hourglassImg} alt="Hourglass icon"/>
                  </div>
              </DialogContent>
          </Dialog>
      </Card>
    );
};

export default UploadPage;
